import { User } from "@supabase/supabase-js";
import { NavigateFunction } from "react-router-dom";
import { useAuthStore } from "src/store/authStore";
import { useProfileStore } from "src/store/profileStore";
import { getRouteForUser } from "src/routes/RouteConfig";
import { UserType, IsOnboarded, USER_TYPE } from "src/context/AuthContext";
import { ProfileService } from "./profileService";

export interface PostAuthFlowParams {
  user: User;
  navigate: NavigateFunction;
  oauthUserType?: string | null;
  source?: string | null;
  refreshUserData: () => Promise<void>;
}

export interface PostAuthFlowResult {
  success: boolean;
  redirectPath?: string;
  error?: string;
}

/**
 * Handles the common post-authentication flow for both OAuth and non-OAuth users
 * This includes fetching user data, profile data, and determining the appropriate redirect
 */
export class PostAuthService {
  /**
   * Execute the complete post-authentication flow
   */
  static async executePostAuthFlow(params: PostAuthFlowParams): Promise<PostAuthFlowResult> {
    const { user, navigate, oauthUserType, source, refreshUserData } = params;

    try {
      console.log("🚀 Starting post-auth flow for user:", user.id);
      console.log("📋 Flow parameters:", { oauthUserType, source });

      // Step 1: Refresh user data to get latest auth state
      console.log("📊 Step 1: Refreshing user data...");
      await refreshUserData();

      // Step 2: Get current user state from store
      const authState = useAuthStore.getState();
      const { userType, isOnboarded, isInitialized } = authState;

      console.log("🔍 Step 2: Current auth state:", {
        userType,
        isOnboarded,
        isInitialized,
      });

      // Step 3: Validate that we have the required data
      if (!isInitialized) {
        console.warn("⚠️ Auth context not initialized, retrying...");
        // Wait a bit and try again
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const retryState = useAuthStore.getState();
        if (!retryState.isInitialized) {
          throw new Error("Authentication context failed to initialize");
        }
      }

      // Step 3.5: Profile data is already fetched by AuthContext during auth refresh
      // Removed duplicate profile fetch to avoid race conditions
      console.log("✅ Step 3.5: Profile data already fetched by AuthContext");

      // Step 4: Handle OAuth-specific validation (if applicable)
      if (oauthUserType) {
        console.log("🔐 Step 4: Validating OAuth user type consistency");
        
        // Validate user type consistency for existing users
        if (userType && isOnboarded && userType !== oauthUserType) {
          console.warn("⚠️ OAuth user type mismatch for existing user:", {
            storedUserType: userType,
            oauthUserType,
            userId: user.id,
          });
          
          console.log("📍 Using stored userType for existing user (ignoring OAuth userType)");
          // Continue with stored userType - don't return early
        }
      } else {
        // Check if this is an OAuth user without oauthUserType parameter (existing user login)
        const isOAuthUser = user.app_metadata?.provider && user.app_metadata.provider !== 'email';
        if (isOAuthUser) {
          console.log("🔐 Step 4: OAuth login detected (existing user without oauthUserType parameter)");
          console.log("📍 Using stored userType from database:", userType);
        }
      }

      // Step 5: Common routing logic for both OAuth and non-OAuth flows
      console.log("🔑 Step 5: Determining redirect path for user");
      const redirectPath = getRouteForUser(userType, isOnboarded);
      console.log("📍 Redirecting to:", redirectPath);

      // Navigate to the determined path
      navigate(redirectPath, { replace: true });

      return {
        success: true,
        redirectPath,
      };

    } catch (error) {
      console.error("❌ Post-auth flow error:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Authentication flow failed",
      };
    }
  }

  /**
   * Handle authentication errors consistently
   */
  static handleAuthError(error: any, navigate: NavigateFunction): void {
    console.error("🚨 Authentication error:", error);

    const errorMessage = error instanceof Error 
      ? error.message 
      : "There was a problem processing your request. Please try again or contact support.";

    localStorage.setItem("confirmationError", errorMessage);

    // Redirect to login page on error
    navigate('/login');
  }

  /**
   * Fetch complete profile data based on user type
   * NOTE: This method is currently not used to avoid race conditions with AuthContext
   */
  private static async fetchCompleteProfileData(userId: string, userType: UserType): Promise<void> {
    console.log("👤 Fetching complete profile data for:", { userId, userType });

    try {
      if (userType === USER_TYPE.STUDENT) {
        await ProfileService.fetchCompleteStudentProfile(userId, userType);
      } else if (userType === USER_TYPE.TUTOR) {
        await ProfileService.fetchCompleteTutorProfile(userId, userType);
      } else {
        console.log("👤 User type doesn't require complete profile fetch:", userType);
      }
    } catch (error) {
      console.error("❌ Error fetching complete profile data:", error);
      // Don't throw here - profile data is not critical for auth flow
    }
  }
}